import { Timers, Timesheets, Timesheet } from '../types/timers'
import { defineStore } from 'pinia'
import { computed, DeepReadonly, ref, watch } from 'vue'
import { useFormatDate } from '../composables/formatDate'
import { useFormatTime } from '../composables/formatTime'
import { Table } from '../types/components/data-rendering'
import { TimesheetsModel } from '../models/TimesheetsModel'
import { createIncompleteTimesheet, useTimers } from './timers'
import { useAllTimers } from './useAllTimers'
import { storeToRefs } from 'pinia'
import { usePayrollWindow } from './usePayrollWindow'
import { format, isSameDay } from 'date-fns'
import { TimersModel } from '../models/TimersModel'
import { TimerAttributes } from '../types/database'
import { isPropertyOfObjectNullish, logInfo } from '../utils'
import { addBreadcrumb } from '@sentry/vue'
import { Notify } from 'quasar'

const timesheets = ref<Timesheets>([])

export const useTimesheets = defineStore('timesheets', () => {
  const { writeTimesheets } = TimesheetsModel()
  watch(
    timesheets,
    async (newData) => {
      //do not save timesheets if skip is flagged
      if (skip.value) {
        skip.value = false
      } else {
        if (newData.map((ts) => ts.editing).includes(true)) return void 0
        await writeTimesheets(JSON.parse(JSON.stringify(newData)))
      }
    },
    { deep: true }
  )
  const skip = ref(false)

  const setTimesheets = (data: Timesheets, options: { skip: boolean } = { skip: false }) => {
    if (options.skip) {
      skip.value = true
    }
    timesheets.value = data
  }
  const noTimesheetForDate = (date: Date) => {
    const timesheetDates = timesheets.value.map((timesheet) => {
      return timesheet.date
    })
    const formattedDate = dateFormat(date)
    return !timesheetDates.includes(formattedDate)
  }
  const removeBeyondPayPeriod = async (timecardDateSkip: string | null = null) => {
    //we actually will only take the ones that are in our current pay period AND the ones from last payroll if its before the cutoff date
    //also keep any prior to these 2 pay periods if the timecard has not been submitted
    if (current.value) {
      timesheets.value = timesheets.value.filter((timesheet) => {
        //keep incomplete timecards! these could be out of the last 2 pay periods, so lets return them first.
        if (timesheet.status == 'incomplete') {
          return true
        }

        // If editing a timecard, we wouldn't want to remove it.
        if (timesheet.date == timecardDateSkip) {
          return true
        }

        //keep ones from last payroll if we aren't past the cutoff of last pay period
        if (new Date().getTime() <= current.value.last_approval_cutoff_date.getTime()) {
          return (
            new Date(timesheet.date).getTime() >= current.value.last_start.getTime()
            // && new Date(timesheet.date).getTime() <= current.value.end.getTime()
          )
        }
        //keep ones in current payroll
        return (
          new Date(timesheet.date).getTime() >= current.value.start.getTime()
          // && new Date(timesheet.date).getTime() <= current.value.end.getTime()
        )
      })

      //now delete associated timers if they have had a timecard generated
      if (new Date().getTime() < current.value.last_approval_cutoff_date.getTime()) {
        const date = current.value.last_approval_cutoff_date
        const removeBefore = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
          2,
          '0'
        )}-${String(date.getDate()).padStart(2, '0')}`
        console.log(removeBefore)
        await TimersModel().deleteFlaggedBeforeDate(removeBefore)
      } else {
        const date = current.value.start
        const removeBefore = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
          2,
          '0'
        )}-${String(date.getDate()).padStart(2, '0')}`
        console.log(removeBefore)
        await TimersModel().deleteFlaggedBeforeDate(removeBefore)
      }
    }
  }
  /**Sort timesheets: most recent first */
  const sortTimesheets = () => {
    timesheets.value.sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime()
    })
  }

  const getTimesheets = computed(() => {
    sortTimesheets()
    return timesheets.value
  })

  const getTimesheet = (index: number) => {
    return timesheets.value[index]
  }

  const updateTimesheet = (index: number, timers: Timers) => {
    if (timesheets.value[index]) {
      timesheets.value[index].timers = timers
    }
  }

  const flagAsModifiedByRep = (index: number) => {
    timesheets.value[index].modifiedByRep = true
  }

  const flagAsModifiedByRepByDate = (date: Date) => {
    const formattedDate = dateFormat(date)
    const index = timesheets.value
      .map((timesheet) => timesheet.date)
      .findIndex((date) => date == formattedDate)
    if (index > -1) {
      flagAsModifiedByRep(index)
    }
  }

  const editTimeSheetByDate = (date: string, timers: DeepReadonly<Timers>) => {
    const tsIndex = timesheets.value.findIndex((timesheet) => timesheet.date == date )
    return editTimesheet(tsIndex, timers)
  }

  const editTimesheet = (index: number, timers: DeepReadonly<Timers>) => {
    if (isPropertyOfObjectNullish(timesheets.value[index], 'timers')) {
      addBreadcrumb({
        category: 'timesheets',
        message: 'timesheet edit failure',
        level: 'error',
        data: { timers, timesheetData: timesheets.value },
      })
      console.trace()
      logInfo('error', 'Timesheet edit failure', 'Timesheet edit failure', {
        timers,
        timesheetData: timesheets.value,
      })
      Notify.create({
        type: 'negative',
        timeout: 0,
        actions: [
          {
            icon: 'close',
            color: 'white',
            // for individual action (button):
            'aria-label': 'Dismiss',
          },
        ],
        position: 'bottom-left',
        message: 'Error editing timesheet. Please try again.',
      })
      return
    } else {
      timesheets.value[index]!.timers = timers
      flagAsModifiedByRep(index)
    }
  }

  const changeStatus = (index: number, status: Timesheet['status']) => {
    timesheets.value.at(index)!.status = status
  }

  const flagReadyToSync = (index: number, data?: Partial<Timesheet>) => {
    if (data) {
      timesheets.value[index]! = { ...timesheets.value[index]!, ...data }
    }
    timesheets.value[index]!.editing = false
    timesheets.value[index]!.readyToSync = true
  }

  const flagReadyToSyncByDate = (date: string, data?: Partial<Timesheet> ) => {
    const tsIndex = timesheets.value.findIndex((timesheet) => timesheet.date == date )
    return flagReadyToSync(tsIndex, data)
  }

  const hasUpdatedTimesheets = computed(() => {
    return timesheets.value.map((timesheet) => timesheet.modifiedByRep).includes(true)
  })

  const hasReadyToSyncTimesheets = computed(() => {
    return timesheets.value.map((timesheet) => timesheet.readyToSync).includes(true)
  })

  const getUpdatedTimesheets = () => {
    return timesheets.value.filter((timesheet) => {
      return timesheet.modifiedByRep === true
    })
  }

  const getReadyToSyncTimesheets = () => {
    return timesheets.value.filter((timesheet) => {
      return timesheet.readyToSync === true
    })
  }

  const hasDenied = computed(() => {
    return timesheets.value
      .map((timesheet) => {
        return timesheet.status
      })
      .includes('declined')
  })

  const resetStoreState = () => {
    setTimesheets([], { skip: true })
  }

  //can only delete timesheets that are incomplete and have no store timers
  const canDelete = (index: number) => {
    const ts = timesheets.value[index]
    console.log(index, ts)
    if (!ts) {
      return false
    } else {
      if (ts.status != 'incomplete') return false
      if (
        ts.timers.some((timer) => {
          return timer.type == 'store'
        })
      )
        return false

      return true
    }
  }
  //hard delete the timecard
  const { deleteByDate } = useAllTimers()
  const markDeleted = (index: number) => {
    //remove from timesheets, first must change status so can navigate away
    timesheets.value[index].status = 'deleted'
  }
  const deleteTimesheets = async () => {
    //find marked deleted and remove
    const toRemove = timesheets.value.filter((timesheet) => {
      return timesheet.status == 'deleted'
    })

    for (const timesheet of toRemove) {
      const date = timesheet.date
      // const date = format(new Date(timesheets.value[index].date), 'MM-dd-yyyy')
      //have to remove associated timers from allTimers
      await deleteByDate(date)
      await TimersModel().deleteTimersById(timesheet.local_timer_id!)
    }

    timesheets.value = timesheets.value.filter((timesheet) => {
      return timesheet.status !== 'deleted'
    })
    //explicitly save timesheets
    await writeTimesheets(timesheets.value)
  }

  /**
   * Grab current timesheet but mark as incomplete, add to app
   * returns true if a timesheet was added.
   */
  const bundleIncompleteTimecard = async () => {
    const { clearAllTimers } = useTimers()
    const { getAllParentTimers, swappedTimers } = storeToRefs(useTimers())
    if (getAllParentTimers.value.length < 1) return false
    window.location.href = '/#/previous_activities'
    await incompleteCheck()
    sortTimesheets()
    //need to reset current days timers, wipe original so re-swap works
    clearAllTimers({ clearOriginal: swappedTimers.value, skipWrite: true })
    return true
  }

  const { current } = storeToRefs(usePayrollWindow())
  const hasIncompleteTimecard = computed(() => {
    //first see if we already have incomplete timecard
    return timesheets.value.some((timesheet) => {
      return timesheet.status === 'incomplete' && !timesheet.readyToSync
    })
  })

  /**Go through time */
  const incompleteCheck = async () => {
    //next, see if somehow we missed generating timecards
    //first get all dates we have timesheets for
    const timecardDates = timesheets.value.map((timesheet) => {
      return format(new Date(timesheet.date), 'MM-dd-yyyy')
    })
    console.log('timecardDates', timecardDates)
    const incomplete: Timesheet[] = []
    const allTimersOnDevice = await TimersModel().getAll()
    console.log('allTimers on device', allTimersOnDevice)
    if (allTimersOnDevice) {
      for (const timers of allTimersOnDevice) {
        const timer_data = JSON.parse(timers.timer_data) as Timers
        let attributes: TimerAttributes | null
        if (timers.attributes && timers.attributes.length) {
          attributes = JSON.parse(timers.attributes)
        } else {
          attributes = null
        }

        console.log('incompleteCheck', {
          date: timers.date,
          timer_data,
          attributes,
          length: timer_data.length,
          sameDay:
            timer_data[0] && timer_data[0].start_time
              ? isSameDay(new Date(timer_data[0].start_time), new Date())
              : 'no timer st',
        })
        //ignore timers with no activities
        if (!timer_data.length) continue
        //skip if today's
        if (isSameDay(new Date(timer_data[0].start_time), new Date())) {
          continue
          //skip if a timecard has been generated at some point, and the device has a record of it
        } else if (attributes && attributes.timecard_generated) {
          continue
        } else {
          //now only return date if its its not in corresponding timecardDates
          const date = format(new Date(timer_data[0].start_time), 'MM-dd-yyyy')
          console.log(date)
          if (!timecardDates.includes(date)) {
            //create an incomplete timesheet
            const newIncomplete = createIncompleteTimesheet(timer_data as Timers, {
              deviceDate: timers.date,
            })
            //add local_timer_id so it is easier to delete
            newIncomplete.local_timer_id = timers.id
            incomplete.push(newIncomplete)
          }
        }
      }
    }
    //add incomplete
    timesheets.value.push(...incomplete)
    sortTimesheets()
  }

  const getIncompleteIndex = () => {
    return timesheets.value.findIndex((timesheet) => timesheet.status === 'incomplete')
  }

  return {
    resetStoreState,
    setTimesheets,
    timesheets,
    noTimesheetForDate,
    getTimesheets,
    getTimesheet,
    updateTimesheet,
    flagReadyToSync,
    flagReadyToSyncByDate,
    flagAsModifiedByRep,
    flagAsModifiedByRepByDate,
    editTimeSheetByDate,
    changeStatus,
    hasUpdatedTimesheets,
    getUpdatedTimesheets,
    hasReadyToSyncTimesheets,
    getReadyToSyncTimesheets,
    hasDenied,
    bundleIncompleteTimecard,
    hasIncompleteTimecard,
    incompleteCheck,
    getIncompleteIndex,
    canDelete,
    markDeleted,
    deleteTimesheets,
    removeBeyondPayPeriod,
  }
})

/**Formats all timecards to a table  */
export const formatTimesheetsForTable = (data: Timesheets): Table => {
  const mappedTS = data.map((timesheet) => {
    const date = useFormatDate(new Date(timesheet.date))
    return {
      date: `${date.weekday()}, ${date.month()} ${date.day()}`,
      time: useFormatTime(timesheet.time, { format: 'h:mm', initTime: 'hours' }),
    }
  })
  return {
    meta: {
      columns: {
        date: {
          label: {
            en_US: 'Date',
            fr_CA: 'Date',
            es_MX: 'Date',
          },
          type: 'string',
        },
        time: {
          label: {
            en_US: 'Time',
            fr_CA: 'Time',
            es_MX: 'Time',
          },
          type: 'number',
        },
      },
    },
    data: mappedTS,
  }
}

const dateFormat = (date: Date) => {
  return format(date, 'MM/dd/yyyy')
}
